import { Link } from "react-router-dom";
import type { Topic } from "@/types";

interface TopicCardProps {
  topic: Topic;
  variant?: "default" | "row";
}

const categoryStyle: Record<string, string> = {
  topics: "bg-primary-100 text-primary-700",
  events: "bg-accent-100 text-accent-700",
  circle: "bg-secondary-100 text-secondary-700",
  opinion: "bg-background-200 text-foreground-700",
};

export function formatHeat(heat: number) {
  if (heat >= 10000) return `${(heat / 10000).toFixed(1)}万`;
  if (heat >= 1000) return `${(heat / 1000).toFixed(1)}k`;
  return `${heat}`;
}

export default function TopicCard({ topic, variant = "default" }: TopicCardProps) {
  if (variant === "row") {
    return (
      <Link
        to={`/topic/${topic.id}`}
        className="group flex gap-4 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300 hover:bg-primary-50/40"
      >
        <div className="h-24 w-32 shrink-0 overflow-hidden rounded-md bg-background-200 sm:h-28 sm:w-40">
          <img
            src={topic.cover}
            alt={`${topic.title} 话题封面`}
            title={`${topic.title} - 吃瓜讨论`}
            className="h-full w-full object-top"
          />
        </div>
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
                categoryStyle[topic.categoryId] || "bg-background-200 text-foreground-700"
              }`}
            >
              {topic.categoryName}
            </span>
            <span className="text-xs text-foreground-600">{topic.time}</span>
          </div>
          <h3 className="mt-2 line-clamp-1 font-heading text-base font-semibold text-foreground-950 transition-colors group-hover:text-primary-700 md:text-lg">
            {topic.title}
          </h3>
          <p className="mt-1.5 line-clamp-2 text-sm leading-6 text-foreground-600">
            {topic.excerpt}
          </p>
          <div className="mt-auto flex items-center gap-4 pt-3 text-xs text-foreground-600">
            <span className="flex items-center gap-1">
              <i className="ri-fire-line text-accent-500"></i>
              {formatHeat(topic.heat)} 热度
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-chat-3-line"></i>
              {topic.comments} 条讨论
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/topic/${topic.id}`}
      className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300"
    >
      <div className="relative h-44 w-full overflow-hidden bg-background-200">
        <img
          src={topic.cover}
          alt={`${topic.title} 话题封面`}
          title={`${topic.title} - 吃瓜讨论`}
          className="h-full w-full object-top transition-transform duration-500 group-hover:scale-105"
        />
        <span
          className={`absolute left-3 top-3 rounded-full px-2.5 py-1 text-xs font-medium ${
            categoryStyle[topic.categoryId] || "bg-background-200 text-foreground-700"
          }`}
        >
          {topic.categoryName}
        </span>
      </div>
      <div className="flex flex-1 flex-col p-5">
        <h3 className="line-clamp-2 font-heading text-lg font-semibold leading-7 text-foreground-950 transition-colors group-hover:text-primary-700">
          {topic.title}
        </h3>
        <p className="mt-3 line-clamp-2 text-sm leading-6 text-foreground-600">
          {topic.excerpt}
        </p>
        <div className="mt-auto flex items-center justify-between gap-3 pt-5 text-xs text-foreground-600">
          <span className="flex items-center gap-1">
            <i className="ri-fire-line text-accent-500"></i>
            {formatHeat(topic.heat)}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-chat-3-line"></i>
            {topic.comments}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-user-line"></i>
            {topic.author}
          </span>
        </div>
      </div>
    </Link>
  );
}